/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect, useMemo, useState} from 'react'
import {useStore} from '../store'
import {getChartData} from '../core/_requests'
import {ChartDataModel} from '../core/_models'
import {
  formatMeasureValue as fmtCell,
  measureValueTitle as cellTitle,
  stripSuffix,
} from '../core/_formats'

type Props = {
  className: string
}

const SLUG = 'overview_summary_table'
// Shown until the config's own name arrives with the data.
const FALLBACK_TITLE = 'Summary Table'

const TableSummary: React.FC<Props> = ({className}) => {
  // The stored config is a pure STWv3 config; the endpoint returns the engine's
  // df as { columns, rows }. We render it transposed — measure columns become
  // rows, the dimension column's values become the day columns.
  const {selectedLocationId, selectedDate} = useStore()
  const [chartData, setChartData] = useState<ChartDataModel | undefined>(undefined)

  useEffect(() => {
    let active = true
    if (selectedLocationId) {
      getChartData(SLUG, selectedLocationId, selectedDate).then((data) => {
        if (active) setChartData(data)
      })
    }
    return () => {
      active = false
    }
  }, [selectedLocationId, selectedDate])

  const wrapper = useMemo(() => {
    const columns = chartData?.columns ?? []
    const rows = chartData?.rows ?? []
    const formats = chartData?.formats ?? {}
    const totalRow = chartData?.total_row
    const dimCol = columns[0]
    const measureCols = columns.slice(1)

    return (
      <div className={`card ${className}`}>
        {/* begin::Header */}
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            {/* Title/subtitle come from the chart config itself; the fallback
                only shows before the first response arrives. */}
            <span className='card-label fw-semibold fs-3 mb-1'>
              {chartData?.name || FALLBACK_TITLE}
            </span>
            <span className='text-muted fw-semibold fs-7'>{chartData?.description || ''}</span>
          </h3>
          <div className='card-toolbar'></div>
        </div>
        {/* end::Header */}
        {/* begin::Body */}
        <div className='card-body py-3'>
          <div className='table-responsive'>
            <table className='table table-row-dashed align-middle text-center gs-0 gy-5'>
              <thead>
                <tr>
                  <th className='p-1'></th>
                  {rows.map((r, i) => (
                    <th className='p-1' key={i}>
                      {dimCol ? String(r[dimCol]) : ''}
                    </th>
                  ))}
                  {totalRow && (
                    <th className='p-1 border-start' key='total'>
                      {dimCol ? String(totalRow[dimCol] ?? 'Total') : 'Total'}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {measureCols.map((col) => (
                  <tr key={col}>
                    <th>{stripSuffix(col)}</th>
                    {/* title is undefined for every format but the weather
                        ones, where the icon is a lossy rendering of the value
                        and the cell is the only place to read it back. */}
                    {rows.map((r, i) => (
                      <th className='p-1' key={i} title={cellTitle(r[col], formats[col])}>
                        {fmtCell(r[col], formats[col])}
                      </th>
                    ))}
                    {totalRow && (
                      <th
                        className='p-1 border-start'
                        key='total'
                        title={cellTitle(totalRow[col], formats[col])}
                      >
                        {fmtCell(totalRow[col], formats[col])}
                      </th>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {/* end::Body */}
      </div>
    )
  }, [chartData, className])

  return wrapper
}

export {TableSummary}
